import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";

import { CampaignImage } from "@/components/campaign-card/campaign-image";
import { CampaignSwitcherList } from "@/components/campaign-switcher-list";
import { cn } from "@/lib/utils";
import { campaignKey } from "@/campaigns";
import { useSwitchableCampaigns } from "@/hooks/use-switchable-campaigns";
import { useSwitcherAttention } from "@/hooks/use-switcher-attention";
import { sheetUnfoldSpring, sideDockEnterAnimation } from "@/utils/animations";

/** Closed: a column of covers. Open: the same rows as the portrait sheet. */
const CLOSED_WIDTH = 64;
const OPEN_WIDTH = 320;

/**
 * The in-game campaign selector on a phone held sideways: the portrait bottom sheet turned 90°.
 *
 * A vertical dock on the LEFT edge, outside the panel (the panel tucks 16px behind it — see
 * `.igw-dock-room` in index.css). Closed, it is a column of campaign covers: the selected one
 * larger with the primary ring, the others smaller and dimmed — one tap on another cover switches
 * straight to it. The selected cover unfolds the dock to the right into the full list (no "+N"
 * pill: every cover is already in the column), over a scrim that dims the panel, for the campaign's name and hook.
 *
 * Same fold-then-commit sequencing as the sheet: the list folds first, the switch lands when
 * the fold is done, so the width spring and the page scale never fight for the frame.
 */
export const InGameCampaignSideDock = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const {
    campaigns,
    selected: selectedCampaign,
    others: otherCampaigns,
    hasMultiple: hasMultipleCampaigns,
    select,
  } = useSwitchableCampaigns();
  // Same news dots as the portrait cover stack, on the circles of the campaigns not shown.
  const attention = useSwitcherAttention(otherCampaigns);

  // Light-dismiss, as the sheet: composedPath (shadow root), Escape swallowed in capture so the
  // first Escape folds the dock and only the next closes the widget.
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const el = dockRef.current;
      if (el && !e.composedPath().includes(el)) setIsOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setIsOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [isOpen]);

  const handleSelect = (key: string) => {
    setPendingKey(key);
    setIsOpen(false);
  };
  const commitPendingSelect = () => {
    if (!pendingKey) return;
    select(pendingKey);
    setPendingKey(null);
  };
  useEffect(() => {
    if (!pendingKey) return;
    const id = window.setTimeout(commitPendingSelect, 180);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- commit reads the latest pending key
  }, [pendingKey]);
  // Rotating back to portrait unmounts this dock mid-fold: land the tapped campaign anyway
  // rather than dropping the pick with the timer.
  const pendingRef = useRef<string | null>(null);
  pendingRef.current = pendingKey;
  const selectRef = useRef(select);
  selectRef.current = select;
  useEffect(
    () => () => {
      if (pendingRef.current) selectRef.current(pendingRef.current);
    },
    []
  );

  if (!selectedCampaign || !hasMultipleCampaigns) return null;

  const selectedKey = campaignKey(selectedCampaign.campaign);

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.14 }}
            onClick={() => setIsOpen(false)}
            className="switcher-scrim absolute inset-0 z-60"
          />
        )}
      </AnimatePresence>
      {/* Reserves the closed dock's width; the open dock grows rightward out of it over the panel. */}
      <div
        ref={dockRef}
        className="absolute inset-y-0 left-0 z-70"
        style={{
          width: CLOSED_WIDTH,
          paddingLeft: "env(safe-area-inset-left, 0px)",
        }}
      >
        <motion.div
          variants={sideDockEnterAnimation}
          initial="initial"
          animate="animate"
          exit="exit"
          className="absolute inset-y-0 left-0"
        >
          <motion.div
            initial={false}
            animate={{ width: isOpen ? OPEN_WIDTH : CLOSED_WIDTH }}
            transition={sheetUnfoldSpring}
            className="dock-side-shadow flex h-full flex-col overflow-hidden rounded-r-3xl border border-l-0 border-white/10 bg-background-card"
          >
            <AnimatePresence
              initial={false}
              mode="wait"
              onExitComplete={commitPendingSelect}
            >
              {isOpen ? (
                <motion.div
                  key="list"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1, transition: { delay: 0.06 } }}
                  exit={{ opacity: 0, transition: { duration: 0.1 } }}
                  className="touch-scroller min-h-0 flex-1 overflow-y-auto"
                  style={{ width: OPEN_WIDTH }}
                >
                  <CampaignSwitcherList
                    campaigns={campaigns}
                    selectedKey={selectedKey}
                    onSelect={handleSelect}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="thumbs"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1, transition: { delay: 0.06 } }}
                  exit={{ opacity: 0, transition: { duration: 0.1 } }}
                  className="touch-scroller flex min-h-0 flex-1 flex-col items-center gap-2 overflow-y-auto py-3"
                  style={{ width: CLOSED_WIDTH }}
                >
                  {/* One circle per campaign, in the list's own order — a switch never reorders
                      or resizes them, only the ring moves: the covers stay where the thumb learnt
                      them. The active circle opens the list (the one with a name worth reading);
                      any other switches straight to it — the cover IS the campaign's identity
                      (same art as the launcher), so no label is needed to pick one. */}
                  {campaigns.map(({ campaign }) => {
                    const key = campaignKey(campaign);
                    const active = key === selectedKey;
                    const finished = campaign.status === "FI";
                    return (
                      <button
                        key={key}
                        ref={active ? triggerRef : undefined}
                        type="button"
                        onClick={() => (active ? setIsOpen(true) : select(key))}
                        aria-haspopup={active ? "listbox" : undefined}
                        aria-expanded={active ? isOpen : undefined}
                        aria-current={active ? "true" : undefined}
                        className={cn(
                          "relative shrink-0 cursor-pointer rounded-full transition-opacity outline-none focus-visible:ring-2 focus-visible:ring-primary",
                          !active && "opacity-60 hover:opacity-100"
                        )}
                      >
                        <CampaignImage
                          campaign={campaign}
                          hideTitle
                          hideDuration
                          className={cn(
                            "size-10 rounded-full border-2",
                            active ? "border-primary" : "border-white/15",
                            finished && "grayscale",
                            active && finished && "opacity-70"
                          )}
                        />
                        {!active && attention[key] && (
                          <span
                            aria-hidden="true"
                            className={cn(
                              "absolute top-0 right-0 size-2.5 rounded-full border-2 border-background-card",
                              attention[key].tone === "urgent"
                                ? "bg-urgent"
                                : "bg-prizes"
                            )}
                          />
                        )}
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      </div>
    </>
  );
};
